import React from 'react';
import { cn } from './cn';

const RUNWAY_TONES = {
  red: 'bg-bun-stage-red-bg text-bun-stage-red-text',
  orange: 'bg-bun-stage-orange-bg text-bun-stage-orange-text',
  yellow: 'bg-bun-stage-yellow-bg text-bun-stage-yellow-text',
  amber: 'bg-bun-stage-amber-bg text-bun-stage-amber-text',
  green: 'bg-bun-stage-green-bg text-bun-stage-green-text',
  blue: 'bg-bun-stage-blue-bg text-bun-stage-blue-text',
  purple: 'bg-bun-stage-purple-bg text-bun-stage-purple-text',
  neutral: 'bg-bun-mist-deep text-bun-slate',
};

/**
 * Bun funnel strip: stage counts joined by hairline connectors. Spec:
 * Pipeline canvas. Numbers stay neutral; the active stage is blue and
 * empty stages fade to faint.
 *
 *   <FunnelStrip
 *     stages={[
 *       { id: "leads", label: "Leads", value: 200, tone: "red" },
 *       { id: "discovery", label: "Discovery", value: 0, tone: "blue" },
 *       { id: "onboarding", label: "Onboarding", value: 164, tone: "blue", active: true },
 *     ]}
 *     onSelect={(id) => ...}
 *   />
 */
export default function FunnelStrip({ stages = [], onSelect, className, ...props }) {
  return (
    <div
      role="list"
      className={cn(
        'flex items-start rounded-bun-lg border border-bun-line px-8 py-6 font-instrument',
        className
      )}
      {...props}
    >
      {stages.map((stage, i) => {
        const hasNoValue = !stage.value;
        const empty = !stage.active && hasNoValue;
        const Tag = onSelect ? 'button' : 'div';
        const toneClasses = RUNWAY_TONES[stage.tone] || RUNWAY_TONES.neutral;
        return (
          <React.Fragment key={stage.id ?? stage.label}>
            {i > 0 && (
              <div
                className="mx-5 mt-[14px] h-px min-w-8 flex-1 bg-bun-line"
                aria-hidden="true"
              />
            )}
            <div role="listitem" className="shrink-0">
              <Tag
                {...(onSelect
                  ? {
                      type: 'button',
                      onClick: () => onSelect(stage.id ?? stage.label),
                      'aria-label': stage.ariaLabel,
                      'aria-pressed': !!stage.active,
                      className: cn(
                        'flex shrink-0 cursor-pointer flex-col items-start gap-2 rounded-bun border-0 bg-transparent p-0 text-left',
                        'focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-bun-blue focus-visible:outline-offset-2'
                      ),
                    }
                  : { className: 'flex shrink-0 flex-col items-start gap-2' })}
              >
                <span
                  className={cn(
                    'text-[28px] font-semibold leading-none tracking-[-0.008em]',
                    stage.active ? 'text-bun-blue' : empty ? 'text-bun-faint' : 'text-bun-text'
                  )}
                >
                  {stage.value}
                </span>
                <span
                  className={cn(
                    'whitespace-nowrap rounded-full px-3 py-[3px] text-bun-heading font-strong tracking-normal',
                    toneClasses,
                    hasNoValue && 'opacity-60'
                  )}
                >
                  {stage.label}
                </span>
              </Tag>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

/** Proportional vertical-funnel step; domain conversion and branches stay in callers. */
FunnelStrip.Step = function FunnelStep({
  value,
  count,
  label,
  detail,
  conversion,
  expanded,
  onToggle,
}) {
  const Tag = onToggle ? 'button' : 'div';
  return (
    <div className="flex w-full flex-col items-center font-instrument">
      {conversion != null && (
        <div className="py-1 text-bun-meta text-bun-stone">↓ {conversion}</div>
      )}
      <Tag
        type={onToggle ? 'button' : undefined}
        onClick={onToggle}
        aria-expanded={onToggle ? expanded : undefined}
        aria-label={onToggle ? label : undefined}
        style={{ width: `${Math.max(6, Math.min(100, value || 0))}%` }}
        className={cn(
          'relative flex min-h-11 min-w-[110px] items-center justify-center rounded-bun bg-bun-data-violet px-3 text-bun-body font-semibold text-bun-on-ink',
          onToggle &&
            'cursor-pointer hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-bun-blue'
        )}
      >
        {count}
        {onToggle && (
          <span aria-hidden="true" className="absolute right-3">
            {expanded ? '▲' : '▼'}
          </span>
        )}
      </Tag>
      <div className="mt-1 text-center text-bun-meta text-bun-stone">
        <span className="font-strong text-bun-slate">{label}</span>
        {detail}
      </div>
    </div>
  );
};
